import { useTranslation } from "react-i18next";
import { useState } from "react";
import type { ManualArElement, ManualBody } from "@arc/contracts";
import type { useManualBodyMutations } from "../useManualMutations.js";
import type { DroppedFile } from "./side-drag.js";
import { bindElementBody, StepArElementBody, StepArStrip } from "./StepArBlock.js";

type Mutations = ReturnType<typeof useManualBodyMutations>;

/**
 * The legacy "Alle Schritte" card (ADR-028): the AR elements bound to NO section and NO step, which Unity shows on
 * every step. Rendered in the main pane in place of a section, from the outline's t("manualEditor.allSteps2") entry.
 *
 * The same AR block as a step card — the element tab strip along the bottom (first in DOM order, painted last, as in
 * `StepCard`), the selected element's body above — and nothing else: the legacy card has no instruction, no media
 * and no main element (`ARLMS_AllSteps.js:56`). Phase 2 wires the manual-level twins of the step writes: every handler
 * here is the all-steps scope, so no `stepId` travels — the editor routes them to `*ManualElement*` mutations.
 */
export function AllStepsCard({
  elements,
  disabled,
  m,
  onAddElement,
  onRemoveElement,
  onDuplicateElement,
  onSetSetting,
  onPickSettingFile,
  copiedFrom,
  onCopySettings,
  onPasteSettings,
  initialStateSensorIds,
}: {
  elements: ManualArElement[];
  disabled: boolean;
  /**
   * The sensors this set is the first to use (ADR-037 D7 / ADR-038 D10). "Alle Schritte" runs on EVERY step, so it
   * precedes them all and is where a device's starting state belongs. Without this the two all-steps sensor elements
   * that already exist in production could never author theirs.
   */
  initialStateSensorIds?: ReadonlySet<number>;
  m: Mutations;
  onAddElement?: () => void;
  onRemoveElement?: (elementId: number) => void;
  onDuplicateElement?: (elementId: number) => Promise<ManualBody | void>;
  onSetSetting?: (elementId: number, name: string, patch: { value?: string | null; fileIds?: number[] }) => void;
  onPickSettingFile?: (elementId: number, name: string, mode: "add" | "replace", currentFileIds: number[], dropped?: DroppedFile) => void;
  copiedFrom?: { elementId: number; label: string; settingNames: string[] } | null;
  onCopySettings?: (elementId: number, label: string, settingNames: string[]) => void;
  onPasteSettings?: (elementId: number) => void;
}) {
  const { t } = useTranslation();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);
  const selected = elements.find((e) => e.id === selectedId) ?? elements[0] ?? null;
  const busy = m.addManualElement.isPending || m.removeManualElement.isPending || m.duplicateManualElement.isPending;
  return (
    <section className="border-border bg-card rounded-xl border shadow-sm" aria-label={t("manualEditor.allSteps")} data-step-id="all-steps">
      <div className="border-border flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
        <h3 className="m-0 text-sm font-medium">{t("manualEditor.allSteps2")}</h3>
        <span className="text-muted-foreground text-xs">{t("manualEditor.shownOnEveryStep")}</span>
      </div>
      <div className="flex flex-col gap-4 p-4">
        <StepArStrip actionsSlotRef={setActionsSlot} elements={elements} disabled={disabled} selectedId={selected?.id ?? null} onSelect={setSelectedId} onAddElement={onAddElement} busy={busy} />
        <div className="flex flex-col gap-4">
          {/* Scoped banners, as on a step card — a failure raised from the strip must render somewhere. */}
          {m.addManualElement.isError ? <p className="text-destructive text-xs">That element couldn't be added.</p> : null}
          {m.removeManualElement.isError ? <p className="text-destructive text-xs">{t("manualEditor.elementRemoveFailed")}</p> : null}
          {m.duplicateManualElement.isError ? <p className="text-destructive text-xs">{t("manualEditor.duplicateFailed")}</p> : null}
          {m.setManualElementSetting.isError ? <p className="text-destructive text-xs">{t("manualEditor.settingSaveFailed")}</p> : null}
          {m.copyManualElementSettings.isError ? <p className="text-destructive text-xs">{t("manualEditor.pasteFailed")}</p> : null}
          {selected ? (
            <StepArElementBody
              key={selected.id}
              actionsSlot={actionsSlot}
              initialStateSensorIds={initialStateSensorIds}
              element={selected}
              siblings={elements}
              disabled={disabled}
              onDelete={onRemoveElement ? () => onRemoveElement(selected.id) : undefined}
              busySetting={
                busy ||
                (m.setManualElementSetting.isPending && m.setManualElementSetting.variables?.elementId === selected.id) ||
                (m.copyManualElementSettings.isPending && m.copyManualElementSettings.variables?.elementId === selected.id)
              }
              copiedFrom={copiedFrom}
              {...bindElementBody({
                element: selected,
                siblings: elements,
                handlers: { onSetSetting, onPickSettingFile, onCopySettings, onPasteSettings, onDuplicate: onDuplicateElement },
                siblingsAfter: (body) => body.allStepsElements,
                onSelect: setSelectedId,
              })}
            />
          ) : (
            /* The legacy's own hint on an empty card (`AllStepsHint`). */
            <p className="text-muted-foreground text-sm">{t("manualEditor.allStepsHint")}</p>
          )}
        </div>
      </div>
    </section>
  );
}
